import type * as React from "react"

import { cn } from "@/lib/utils"

import { Button } from "./button"

const EXPAND_MS = 300

const BUTTON_TRANSITION = `grid-template-columns ${EXPAND_MS}ms linear, background-color 500ms linear, color 500ms linear, box-shadow 500ms linear, border-color 500ms linear`

export type AnimatedButtonProps = Omit<
  React.ComponentProps<typeof Button>,
  "asChild" | "size"
> & {
  icon: React.ReactNode
}

function AnimatedButton({
  className,
  icon,
  children,
  style,
  ...props
}: AnimatedButtonProps) {
  return (
    <Button
      {...props}
      data-slot="animated-button"
      className={cn(
        "group relative inline-grid h-9 max-w-full cursor-pointer overflow-hidden p-0 !px-0 !py-0 shadow-none",
        "has-[>svg]:!px-0 gap-0",
        "grid-cols-[minmax(2.25rem,2.25rem)_minmax(0,0fr)] hover:grid-cols-[minmax(2.25rem,2.25rem)_minmax(0,1fr)]",
        className
      )}
      style={{ ...style, transition: BUTTON_TRANSITION }}
    >
      <span className="flex size-full items-center justify-center [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 [&_svg]:shrink-0">
        {icon}
      </span>
      <span className="flex min-h-9 min-w-0 items-center overflow-hidden pr-3 pl-0.5">
        <span
          className={cn(
            "inline-block max-w-full translate-x-full truncate text-sm font-medium whitespace-nowrap opacity-0",
            "transition-[transform,opacity] ease-linear motion-reduce:transition-none",
            "group-hover:translate-x-0 group-hover:opacity-100"
          )}
          style={{ transitionDuration: `${EXPAND_MS}ms` }}
        >
          {children}
        </span>
      </span>
    </Button>
  )
}

export { AnimatedButton }
